import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Button } from "../components/ui/button";
import { RANK_STAGES, NETWORK_COMPLIANCE } from "../lib/data";
import { publicRanks } from "../lib/api";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.05 } }) };
const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function RanksRewards() {
  const [ranks, setRanks] = useState([]);
  useEffect(() => { publicRanks().then(setRanks).catch(() => {}); }, []);

  return (
    <div data-testid="ranks-rewards-page" className="bg-cream-100 text-soil-700 pt-24 pb-24">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-10 text-center">
        <div className="max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-terracotta-600 mb-3">Genesis Business Network</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-forest-800">Grow With <span className="saffron-text">Genesis</span></h1>
          <p className="mt-4 text-lg text-soil-600">Advance through Genesis ranks as your team and activity grow. Every reward is verified and approved before payout.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {RANK_STAGES.map((st, i) => (
            <motion.div key={st.k} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="card-cream rounded-2xl p-5 text-center">
              <div className="w-11 h-11 mx-auto rounded-full forest-gradient flex items-center justify-center font-mono-fin font-bold text-cream-50 mb-3">{i + 1}</div>
              <div className="font-heading text-lg font-extrabold text-terracotta-600">{st.k}</div>
              <div className="text-soil-500 text-xs mt-1">{st.d}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-8">
        <div className="text-center mb-8"><div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta-600 mb-3">The Genesis Rank Ladder</div><h2 className="font-heading text-2xl sm:text-3xl font-bold text-forest-800">Ranks &amp; Rewards</h2><p className="text-soil-600 mt-2">Live rank tiers configured by Genesis Group.</p></div>
        {ranks.length === 0 ? (
          <div className="card-cream rounded-2xl p-10 text-center text-soil-500" data-testid="ranks-empty">Rank tiers are being finalised. Please check back soon.</div>
        ) : (
          <div className="space-y-3" data-testid="ranks-list">
            {ranks.map((r, i) => (
              <div key={r.id || r.name || i} className="card-cream rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3" data-testid={`rank-row-${i}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-forest-100 flex items-center justify-center"><Icons.Trophy className="w-5 h-5 text-forest-600" /></div>
                  <div>
                    <div className="font-heading font-bold text-forest-800">{r.name}</div>
                    <div className="text-soil-500 text-xs mt-0.5">{(r.min_directs ?? 0)} direct{(r.min_directs ?? 0) === 1 ? "" : "s"} · {money(r.min_team)} team business</div>
                  </div>
                </div>
                {Number(r.reward || r.reward_amount || 0) > 0 && (
                  <span className="text-sm font-semibold px-3 py-1 rounded-full bg-saffron-100 text-terracotta-700 border border-saffron-500/30">Reward {money(r.reward || r.reward_amount)}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10 text-center">
        <div className="card-cream rounded-2xl p-6">
          <p className="text-soil-700 text-sm" data-testid="ranks-approval-notice">All rewards require milestone verification and administrative approval before payout disbursement — Genesis never auto-pays.</p>
          <p className="text-soil-500 text-xs mt-3">{NETWORK_COMPLIANCE}</p>
          <Link to="/register" data-testid="ranks-register-cta" className="inline-block mt-6"><Button className="forest-gradient text-cream-50 font-bold h-11 px-7 rounded-full">Start Your Journey</Button></Link>
        </div>
      </section>
    </div>
  );
}
